import { US_STATE_CODES } from '@lib/constants/us-state-codes';

import {
  CURRENT_TOOL_OPTIONS,
  REAL_ESTATE_TOOL_IDS,
  type SetupOption,
} from './setup-options';

/**
 * Industry profiles drive the industry-specific part of signup: which roles
 * we offer, whether we ask for a license, which state form libraries apply,
 * and which template tags we use to surface starter templates. Adding an
 * industry means adding one entry here — the flow picks it up automatically.
 */

type IndustryId =
  | 'residential_real_estate'
  | 'commercial_real_estate'
  | 'property_management'
  | 'mortgage'
  | 'title_escrow'
  | 'legal'
  | 'general';

export interface StateFormLibrary {
  /** Shown to the user, e.g. "Florida Real Estate Forms". */
  name: string;
  /** Who we check the license against, e.g. "DBPR". */
  registry: string;
  /** Sample titles for the preview; not an exhaustive catalog. */
  sampleForms: readonly string[];
}

export interface IndustryProfile {
  id: IndustryId;
  /** Tested against the company type label the user picked or typed. */
  matches: RegExp;
  /** Role options for "What's your role?" (falls back to generic roles). */
  roles?: readonly SetupOption[];
  /** "What will you use Clai for?" options, shown right after industry. */
  useCases: readonly SetupOption[];
  /** Tags used to pick starter templates for this industry. */
  templateTags: readonly string[];
  /** Template names shown in previews until real tagged templates exist. */
  sampleTemplates: readonly string[];
  /** A realistic example deal name used in previews, e.g. "123 Main St". */
  sampleDeal: string;
  licensing?: {
    /** Roles that hold a license and are asked for the number. */
    licensedRoles: readonly string[];
    /** State form libraries unlocked by a verified license, by USPS code. */
    stateForms: Partial<Record<string, StateFormLibrary>>;
  };
  /** Roles offered the free Form Concierge (team plan) during signup. */
  conciergeRoles?: readonly string[];
}

const RESIDENTIAL_ROLES: readonly SetupOption[] = [
  { id: 'agent', label: 'Agent' },
  { id: 'broker', label: 'Broker' },
  { id: 'transaction_coordinator', label: 'Transaction coordinator' },
  { id: 'office_manager', label: 'Office manager' },
  { id: 'other', label: 'Something else' },
];

const GENERIC_ROLES: readonly SetupOption[] = [
  { id: 'owner', label: 'Owner / Founder' },
  { id: 'operations', label: 'Operations / Admin' },
  { id: 'finance', label: 'Finance / Accounting' },
  { id: 'sales', label: 'Sales / Business development' },
  { id: 'attorney', label: 'Attorney / Paralegal' },
  { id: 'other', label: 'Something else' },
];

// Ordered specific-first: the first matching profile wins.
const INDUSTRY_PROFILES: readonly IndustryProfile[] = [
  {
    id: 'commercial_real_estate',
    matches: /commercial/i,
    roles: RESIDENTIAL_ROLES,
    useCases: [
      { id: 'lois_leases', label: 'LOIs and leases' },
      { id: 'purchase_sale', label: 'Purchase and sale agreements' },
      { id: 'due_diligence', label: 'Due diligence tracking' },
      { id: 'tenant_onboarding', label: 'Tenant onboarding' },
      { id: 'deposits_rent', label: 'Deposits and rent collection' },
    ],
    templateTags: ['real_estate', 'commercial'],
    sampleTemplates: [
      'LOI',
      'Commercial lease',
      'Purchase & sale agreement',
      'Estoppel',
    ],
    sampleDeal: 'Suite 400 · Office lease',
  },
  {
    id: 'property_management',
    matches: /property management/i,
    useCases: [
      { id: 'lease_signing', label: 'Lease signing and renewals' },
      { id: 'applications', label: 'Rental applications' },
      { id: 'inspections', label: 'Move-in and move-out inspections' },
      { id: 'owner_agreements', label: 'Owner management agreements' },
      { id: 'rent_deposits', label: 'Rent and deposit collection' },
    ],
    templateTags: ['real_estate', 'property_management'],
    sampleTemplates: [
      'Residential lease',
      'Renewal notice',
      'Move-in checklist',
      'Owner agreement',
    ],
    sampleDeal: 'Unit 2B · Lease renewal',
  },
  {
    id: 'mortgage',
    matches: /mortgage|lend|bank|credit union/i,
    useCases: [
      { id: 'loan_applications', label: 'Loan applications' },
      { id: 'disclosures', label: 'Disclosures' },
      { id: 'document_collection', label: 'Borrower document collection' },
      { id: 'closing_coordination', label: 'Closing coordination' },
    ],
    templateTags: ['mortgage'],
    sampleTemplates: ['Loan application', 'Rate lock', 'Disclosure package'],
    sampleDeal: 'Garcia · Refinance',
  },
  {
    id: 'title_escrow',
    matches: /title|escrow/i,
    useCases: [
      { id: 'escrow_instructions', label: 'Escrow instructions' },
      { id: 'closing_documents', label: 'Closing documents' },
      { id: 'wire_authorization', label: 'Wire authorizations' },
      { id: 'document_collection', label: 'Document collection' },
    ],
    templateTags: ['title_escrow'],
    sampleTemplates: [
      'Escrow instructions',
      'Closing statement',
      'Wire authorization',
    ],
    sampleDeal: 'Escrow #4821 · 88 Oak Ave',
  },
  {
    id: 'residential_real_estate',
    matches: /residential|brokerage|realt|real estate agent/i,
    roles: RESIDENTIAL_ROLES,
    useCases: [
      { id: 'listings', label: 'Listing agreements and disclosures' },
      { id: 'offers', label: 'Buyer offers and contracts' },
      { id: 'transactions', label: 'Transaction management and deadlines' },
      { id: 'compliance', label: 'Brokerage compliance and file review' },
      { id: 'payments', label: 'Commissions and earnest money' },
      { id: 'agent_onboarding', label: 'Agent onboarding paperwork' },
    ],
    templateTags: ['real_estate', 'residential'],
    sampleTemplates: [
      'Listing agreement',
      'Purchase agreement',
      'Buyer brokerage agreement',
      'Seller disclosure',
    ],
    sampleDeal: '123 Main St · Buyer offer',
    licensing: {
      licensedRoles: ['agent', 'broker'],
      stateForms: {
        FL: {
          name: 'Florida Real Estate Forms',
          registry: 'DBPR',
          sampleForms: [
            'AS IS Residential Contract',
            'Residential Contract for Sale and Purchase',
            'Exclusive Right of Sale Listing Agreement',
            "Seller's Property Disclosure",
            'Exclusive Buyer Brokerage Agreement',
            'Lead-Based Paint Disclosure',
          ],
        },
      },
    },
    conciergeRoles: ['broker'],
  },
  {
    id: 'legal',
    matches: /law|legal|attorney/i,
    useCases: [
      { id: 'engagement', label: 'Engagement letters' },
      { id: 'ndas', label: 'NDAs' },
      { id: 'intake', label: 'Client intake' },
      { id: 'settlements', label: 'Settlements' },
      { id: 'retainers', label: 'Retainers and payments' },
    ],
    templateTags: ['legal'],
    sampleTemplates: [
      'Engagement letter',
      'NDA',
      'Settlement agreement',
      'Retainer',
    ],
    sampleDeal: 'Johnson · Retainer',
  },
];

const GENERAL_PROFILE: IndustryProfile = {
  id: 'general',
  matches: /.*/,
  useCases: [
    { id: 'contracts', label: 'Contracts and agreements' },
    { id: 'client_intake', label: 'Client onboarding and intake' },
    { id: 'proposals', label: 'Proposals and quotes' },
    { id: 'hr', label: 'HR and employee paperwork' },
    { id: 'invoices', label: 'Invoices and payments' },
  ],
  templateTags: ['general'],
  sampleTemplates: ['NDA', 'Service agreement', 'Invoice', 'Intake form'],
  sampleDeal: 'Acme · Service agreement',
};

export function getIndustryProfile(companyType: string): IndustryProfile {
  const type = companyType.trim();
  if (!type) return GENERAL_PROFILE;
  return INDUSTRY_PROFILES.find(p => p.matches.test(type)) ?? GENERAL_PROFILE;
}

/** Roles to offer for the user's industry. */
export function rolesFor(companyType: string): readonly SetupOption[] {
  return getIndustryProfile(companyType).roles ?? GENERIC_ROLES;
}

export function asksForLicense(companyType: string): boolean {
  return !!getIndustryProfile(companyType).licensing;
}

export function isLicensedRole(companyType: string, role: string): boolean {
  return !!getIndustryProfile(companyType).licensing?.licensedRoles.includes(
    role
  );
}

export function stateFormLibrary(
  companyType: string,
  stateCode: string
): StateFormLibrary | null {
  return (
    getIndustryProfile(companyType).licensing?.stateForms[stateCode] ?? null
  );
}

/**
 * Matched on the type itself, not the profile: the commercial
 * profile's /commercial/ also catches "Commercial cleaning".
 */
export function isRealEstateIndustry(companyType: string): boolean {
  return /real\s*estate|realt|property management/i.test(companyType);
}

/** "Which tools are you replacing?" options for the user's industry. */
export function currentToolOptions(
  companyType: string
): readonly SetupOption[] {
  if (isRealEstateIndustry(companyType)) return CURRENT_TOOL_OPTIONS;
  return CURRENT_TOOL_OPTIONS.filter(
    tool => !REAL_ESTATE_TOOL_IDS.includes(tool.id)
  );
}

export function offersFormConcierge(
  companyType: string,
  role: string
): boolean {
  return !!getIndustryProfile(companyType).conciergeRoles?.includes(role);
}

function titleCase(name: string): string {
  return name.replace(/\b\w/g, c => c.toUpperCase());
}

/** 50 states + DC, sorted by name, for the "licensed in" dropdown. */
export const US_STATE_OPTIONS: readonly SetupOption[] = Object.entries(
  US_STATE_CODES
)
  .map(([name, code]) => ({ id: code, label: titleCase(name) }))
  .sort((a, b) => a.label.localeCompare(b.label));

/**
 * Best guess at the user's state from what we already know: the company
 * address first ("…, Miami, FL 33101" or "…, Florida"), then the IP city
 * hint ("Austin, TX"). Returns a USPS code or ''.
 */
export function guessStateCode(
  ...sources: ReadonlyArray<string | null | undefined>
): string {
  const codes = new Set(Object.values(US_STATE_CODES));
  for (const source of sources) {
    if (!source) continue;
    const codeMatch = source.match(/,\s*([A-Z]{2})\b(?:\s+\d{5})?/);
    if (codeMatch && codes.has(codeMatch[1])) return codeMatch[1];
    const lower = source.toLowerCase();
    const named = Object.entries(US_STATE_CODES).find(([name]) =>
      new RegExp(`\\b${name}\\b`).test(lower)
    );
    if (named) return named[1];
  }
  return '';
}

/** Always offered last: some people just want a better e-signature tool. */
const ESIGN_REPLACEMENT_USE_CASE: SetupOption = {
  id: 'esign_replacement',
  label: 'Just a modern replacement for my eSignature tool',
  description: 'Send, sign and track documents — minus the clunky legacy tool.',
};

/** Use-case options for the user's industry, plus the e-signature option. */
export function industryUseCases(companyType: string): readonly SetupOption[] {
  return [
    ...getIndustryProfile(companyType).useCases,
    ESIGN_REPLACEMENT_USE_CASE,
  ];
}
